// src/features/october/hooks/useProducts.js
import { useState, useEffect, useCallback, useMemo } from 'react';
import { ProductService } from '../services/productService';

export const useProducts = (filters = {}) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      let data;
      if (filters.category && filters.category !== 'all') {
        data = await ProductService.getProductsByCategory(filters.category);
      } else if (filters.gender && filters.gender !== 'all') {
        data = await ProductService.getProductsByGender(filters.gender);
      } else if (filters.inStockOnly) {
        data = await ProductService.getInStockProducts();
      } else {
        data = await ProductService.getAllProducts();
      }
      
      setProducts(data);
    } catch (err) {
      setError('Failed to fetch products');
      console.error('Error fetching products:', err);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [filters.category, filters.gender, filters.inStockOnly]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const refetch = useCallback(() => {
    return fetchProducts();
  }, [fetchProducts]);

  // Apply client-side filtering
  const filteredProducts = useMemo(() => {
    if (!products.length) return [];
    return ProductService.filterProducts(products, filters);
  }, [products, filters]);

  // Apply sorting
  const sortedProducts = useMemo(() => {
    if (!filteredProducts.length || !filters.sortBy) return filteredProducts;
    return ProductService.sortProducts(filteredProducts, filters.sortBy);
  }, [filteredProducts, filters.sortBy]);

  return {
    products: sortedProducts,
    allProducts: products,
    loading,
    error,
    refetch,
    isEmpty: !loading && sortedProducts.length === 0,
    total: sortedProducts.length
  };
};

export const useProduct = (productId) => {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchProduct = useCallback(async () => {
    if (!productId) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      const data = await ProductService.getProductById(productId);
      setProduct(data);
    } catch (err) {
      setError('Failed to fetch product');
      console.error('Error fetching product:', err);
      setProduct(null);
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    fetchProduct();
  }, [fetchProduct]);

  const refetch = useCallback(() => {
    return fetchProduct();
  }, [fetchProduct]);

  return {
    product,
    loading,
    error,
    refetch,
    exists: !loading && product !== null
  };
};

export const useFeaturedProducts = (limit = 4) => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchFeaturedProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await ProductService.getFeaturedProducts(limit);
      setFeaturedProducts(data);
    } catch (err) {
      setError('Failed to fetch featured products');
      console.error('Error fetching featured products:', err);
      setFeaturedProducts([]);
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    fetchFeaturedProducts();
  }, [fetchFeaturedProducts]);

  const refetch = useCallback(() => {
    return fetchFeaturedProducts();
  }, [fetchFeaturedProducts]);

  return {
    featuredProducts,
    loading,
    error,
    refetch,
    isEmpty: !loading && featuredProducts.length === 0
  };
};

export const useProductSearch = () => {
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const searchProducts = useCallback(async (term) => {
    const trimmedTerm = term?.trim() || '';
    setSearchTerm(trimmedTerm);

    if (!trimmedTerm) {
      setSearchResults([]);
      setError(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const results = await ProductService.searchProducts(trimmedTerm);
      setSearchResults(results);
    } catch (err) {
      setError('Failed to search products');
      console.error('Error searching products:', err);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const clearSearch = useCallback(() => {
    setSearchResults([]);
    setSearchTerm('');
    setError(null);
  }, []);

  return {
    searchResults,
    loading,
    error,
    searchTerm,
    searchProducts,
    clearSearch,
    hasResults: searchResults.length > 0,
    isEmpty: !loading && searchTerm && searchResults.length === 0
  };
};

export const useRelatedProducts = (productId, limit = 4) => {
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchRelatedProducts = useCallback(async () => {
    if (!productId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await ProductService.getRelatedProducts(productId, limit);
      setRelatedProducts(data);
    } catch (err) {
      setError('Failed to fetch related products');
      console.error('Error fetching related products:', err);
      setRelatedProducts([]);
    } finally {
      setLoading(false);
    }
  }, [productId, limit]);

  useEffect(() => {
    fetchRelatedProducts();
  }, [fetchRelatedProducts]);

  const refetch = useCallback(() => {
    return fetchRelatedProducts();
  }, [fetchRelatedProducts]);

  return {
    relatedProducts,
    loading,
    error,
    refetch,
    isEmpty: !loading && relatedProducts.length === 0
  };
};

export const useProductStats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await ProductService.getProductStats();
      setStats(data);
    } catch (err) {
      setError('Failed to fetch product statistics');
      console.error('Error fetching product stats:', err);
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const refetch = useCallback(() => {
    return fetchStats();
  }, [fetchStats]);

  return {
    stats,
    loading,
    error,
    refetch
  };
};

// Hook for managing product filters
export const useProductFilters = (initialFilters = {}) => {
  const [filters, setFilters] = useState({
    category: 'all',
    gender: 'all',
    type: 'all',
    intensity: 'all',
    minPrice: null,
    maxPrice: null,
    inStockOnly: false,
    featuredOnly: false,
    sortBy: 'newest',
    ...initialFilters
  });

  const updateFilter = useCallback((key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  }, []);

  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters
    }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters({
      category: 'all',
      gender: 'all',
      type: 'all',
      intensity: 'all',
      minPrice: null,
      maxPrice: null,
      inStockOnly: false,
      featuredOnly: false,
      sortBy: 'newest'
    });
  }, []);

  const hasActiveFilters = useMemo(() => {
    return filters.category !== 'all' ||
           filters.gender !== 'all' ||
           filters.type !== 'all' ||
           filters.intensity !== 'all' ||
           filters.minPrice !== null ||
           filters.maxPrice !== null ||
           filters.inStockOnly ||
           filters.featuredOnly;
  }, [filters]);

  return {
    filters,
    updateFilter,
    updateFilters,
    resetFilters,
    hasActiveFilters
  };
};